import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type { User } from '@supabase/supabase-js';

import { useAuth } from '@/hooks/useAuth';
import { shouldRetryQuery } from '@/lib/queryRetry';
import { fetchBootContext, type BootContext } from '@/services/bootContextService';

export const BOOT_CONTEXT_KEY = 'boot-context';

/**
 * The ONE boot query (identity + role + own location account + restriction
 * flag). useCurrentUserStaff, useRestrictedStaff and LocationSessionProvider
 * select from it, so the app's gates resolve from a single round trip.
 *
 * Throws on any RPC error (never null-defaults): an errored boot has no
 * data, and every gate treats "no data" as unresolved — LocationGate shows a
 * retry instead of the app. queryClient.clear() on account change
 * (App.tsx) drops it with everything else.
 */
export function useBootContext(): {
  query: UseQueryResult<BootContext, Error>;
  user: User | null;
  authLoading: boolean;
} {
  const { user, loading: authLoading } = useAuth();
  const query = useQuery<BootContext, Error>({
    queryKey: [BOOT_CONTEXT_KEY, user?.id ?? null],
    queryFn: fetchBootContext,
    enabled: !!user,
    staleTime: 5 * 60_000,
    gcTime: 15 * 60_000,
    // LocationGate blocks every user on this query — one retry (the old
    // viewer-context query's budget) instead of the default three, and none
    // for a deterministic permission error.
    retry: (failureCount, error) => failureCount < 1 && shouldRetryQuery(failureCount, error),
    // After a failed boot the gates swap loader ↔ retry screen, mounting new
    // observers; with the default retryOnMount each mount would refetch,
    // flip the query back to pending and loop. A failed boot waits for the
    // explicit Retry (or a window-focus refetch) instead.
    retryOnMount: false,
  });
  return { query, user, authLoading };
}
